import { computed, type IComputedValue, type IObservableValue, observable, runInAction } from "mobx"

import { ServiceItemModel } from "@/components/Chat/NewProject/ServiceItemModel.ts"
import { type OrgContext, withGlobalLoader } from "@/lib/context.ts"
import { submitGeneric } from "@/lib/formUtils.ts"
import { Model } from "@/lib/Model.ts"

import type { AiChatMessageContent, InitRuntimeConfigTaskState } from "@/lib/client/generated/TypeDefinitions.ts"

export type ServiceListModelParams = {
  content: AiChatMessageContent.InitRuntimeConfig.ServiceList
  context: OrgContext
  taskId: string
  isAgentRunning: IObservableValue<boolean>
}

export class ServiceListModel extends Model {
  readonly services: ServiceItemModel[]
  readonly isSubmitting = observable.box(false)
  readonly processing: IComputedValue<boolean>
  readonly confirmed = observable.box(false)
  readonly context: OrgContext
  readonly taskId: string
  readonly isInEditMode = observable.box(false)
  readonly isManuallyEnteredEditMode = observable.box(false)
  readonly canEnterEditMode: IComputedValue<boolean>
  readonly agentIsRunning: IComputedValue<boolean>

  constructor(
    params: ServiceListModelParams
  ) {
    super("ServiceListModel")

    this.context = params.context
    this.taskId = params.taskId
    this.confirmed.set(params.content.confirmed)
    this.agentIsRunning = params.isAgentRunning
    this.processing = computed(() => {
      return this.isSubmitting.get() || params.isAgentRunning.get()
    })
    this.canEnterEditMode = computed(() => {
      return this.confirmed.get()
    })
    this.isInEditMode = computed(() => {
      return !this.confirmed.get() || this.isManuallyEnteredEditMode.get() && this.canEnterEditMode.get()
    })

    this.services = params.content.services.map(it => new ServiceItemModel(it, this.isInEditMode))
  }

  readonly validationError = computed(() => {
    if (this.getEditedServices().filter(it => !it.skipped).length === 0) {
      return "Choose at least one service"
    }
    return null
  })

  readonly canSubmit = computed(() => {
    return this.validationError.get() === null && !this.processing.get()
  })

  readonly submitButtonText = computed(() => {
    const validationError = this.validationError.get()
    if (validationError) {
      return validationError
    }
    return this.confirmed.get() ? "Update" : "Continue"
  })

  readonly setEditMode = (value: boolean) => {
    runInAction(() => {
      this.isManuallyEnteredEditMode.set(value)
    })
  }

  readonly reset = () => {
    this.setEditMode(false)
    this.services.forEach(it => it.reset())
  }

  getEditedServices = () => {
    const services: InitRuntimeConfigTaskState.Service[] = []
    for (const serviceModel of this.services) {
      services.push(serviceModel.toDto())
    }
    return services
  }

  handleContinue = async () => {
    await submitGeneric({
      isSubmitting: this.isSubmitting,
      context: this.context,
      submit: async () => {
        const services = this.getEditedServices()
        await withGlobalLoader(this.context, this.context.queries.apiClient.continuePreviewRuntimeConfigWithServices(this.taskId, services))
      }
    })
  }
}